import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on, StoreModule } from '@ngrx/store';
import { ActiveAlertContract } from '@polaris-customerweb/sharedcomponents.core';
import { Vehicle } from '@polaris-customerweb/sharedcomponents.garage';
import * as VehicleActions from 'src/app/state/vehicle/vehicle.actions';
import { EffectLoadingState } from '../shared/models/effect-state';
import { EffectStateBase } from '../shared/models/effect-state-base';
import {
    VehicleCategory,
    VehicleMake,
    VehicleModel,
    VehicleYear,
} from './models/industry-dropdowns.model';

// This is definition of how state is stored/serialized in the store.

export const vehicleFeatureKey = 'vehicleState';

export interface VehicleCategoryState extends EffectStateBase {
    data: VehicleCategory | null;
}

export interface VehicleMakeState
    extends EntityState<VehicleMake>,
        EffectStateBase {
    selectedCategoryId: string;
}

export const makeAdapter: EntityAdapter<VehicleMake> =
    createEntityAdapter<VehicleMake>();

export interface VehicleYearState
    extends EntityState<VehicleYear>,
        EffectStateBase {
    selectedMakeId: string;
}

export const yearAdapter: EntityAdapter<VehicleYear> =
    createEntityAdapter<VehicleYear>();

export interface VehicleModelState
    extends EntityState<VehicleModel>,
        EffectStateBase {
    selectedMakeYearId: string;
}

export const modelAdapter: EntityAdapter<VehicleModel> =
    createEntityAdapter<VehicleModel>();

export interface VehicleState {
    activeVehicleId: string;
    areVehiclesLoaded: boolean;
    vehiclesForUser: Vehicle[];
    vehicleCategories: VehicleCategoryState;
    vehicleMakes: VehicleMakeState;
    vehicleYears: VehicleYearState;
    vehicleModels: VehicleModelState;
}

export const initialState: VehicleState = {
    activeVehicleId: '',
    areVehiclesLoaded: false,
    vehicleCategories: {
        data: null,
        effectState: EffectLoadingState.Init,
    },
    vehicleMakes: makeAdapter.getInitialState({
        effectState: EffectLoadingState.Init,
        selectedCategoryId: '',
    }),
    vehicleModels: modelAdapter.getInitialState({
        effectState: EffectLoadingState.Init,
        selectedMakeYearId: '',
    }),
    vehicleYears: yearAdapter.getInitialState({
        effectState: EffectLoadingState.Init,
        selectedMakeId: '',
    }),
    vehiclesForUser: [],
};

// Reducer takes a state and an action. And performs the action on that state.
// Return value of this reducer/function becomes the new state.
export const vehicleReducer = createReducer(
    initialState,
    on(VehicleActions.GetUserVehiclesSuccess, (state, { vehicles }) => {
        const activeVehicleId =
            vehicles.length === 0 ? '' : state.activeVehicleId;

        return {
            ...state,
            activeVehicleId,
            areVehiclesLoaded: true,
            vehiclesForUser: vehicles,
        };
    }),
    on(VehicleActions.SetVehiclesForUser, (state, { vehicles }) => {
        const activeVehicleId =
            vehicles.length === 0 ? '' : state.activeVehicleId;
        return {
            ...state,
            activeVehicleId,
            vehiclesForUser: vehicles,
        };
    }),
    on(VehicleActions.AddVehicle, (state, { vehicle }) => {
        return {
            ...state,
            activeVehicleId: vehicle.id,
            vehiclesForUser: [vehicle, ...state.vehiclesForUser],
        };
    }),
    on(VehicleActions.UpdateVehicle, (state, { vehicle }) => {
        const vehiclesForUser = state.vehiclesForUser.map((veh) => {
            if (veh.id === vehicle.id) {
                return vehicle;
            }
            return veh;
        });
        return { ...state, vehiclesForUser };
    }),
    on(VehicleActions.DeleteVehicle, (state, { id }) => {
        return {
            ...state,
            activeVehicleId:
                state.activeVehicleId === id ? '' : state.activeVehicleId,
            vehiclesForUser: state.vehiclesForUser.filter(
                (vehicle) => vehicle.id !== id
            ),
        };
    }),
    on(VehicleActions.InvalidateVehicleCache, (state) => {
        return {
            ...state,
            areVehiclesLoaded: false,
        };
    }),
    on(VehicleActions.SetActiveVehicleId, (state, { vehicleId }) => {
        return {
            ...state,
            activeVehicleId: vehicleId,
        };
    }),

    on(VehicleActions.GetVehicleCategories, (state) => {
        return {
            ...state,
            vehicleCategories: {
                ...state.vehicleCategories,
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(
        VehicleActions.GetVehicleCategoriesSuccess,
        (state, { vehicleCategories }) => {
            return {
                ...state,
                vehicleCategories: {
                    ...state.vehicleCategories,
                    data: vehicleCategories,
                    effectState: EffectLoadingState.Loaded,
                },
            };
        }
    ),
    on(VehicleActions.GetVehicleCategoriesFail, (state, { errorMessage }) => {
        return {
            ...state,
            vehicleCategories: {
                ...state.vehicleCategories,
                effectState: { errorMessage },
            },
        };
    }),
    on(VehicleActions.AddVehicleSelectCategory, (state, { categoryId }) => {
        return {
            ...state,
            vehicleMakes: {
                ...state.vehicleMakes,
                selectedCategoryId: categoryId,
            },
        };
    }),
    on(VehicleActions.GetVehicleMakes, (state) => {
        return {
            ...state,
            vehicleMakes: {
                ...state.vehicleMakes,
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(VehicleActions.GetVehicleMakesSuccess, (state, { vehicleMakes }) => {
        return {
            ...state,
            vehicleMakes: makeAdapter.addOne(vehicleMakes, {
                ...state.vehicleMakes,
                effectState: EffectLoadingState.Loaded,
            }),
        };
    }),
    on(VehicleActions.GetVehicleMakesFail, (state, { errorMessage }) => {
        return {
            ...state,
            vehicleMakes: {
                ...state.vehicleMakes,
                effectState: { errorMessage },
            },
        };
    }),
    on(VehicleActions.AddVehicleSelectMake, (state, { makeId }) => {
        return {
            ...state,
            vehicleYears: {
                ...state.vehicleYears,
                selectedMakeId: makeId,
            },
        };
    }),
    on(VehicleActions.GetVehicleYears, (state) => {
        return {
            ...state,
            vehicleYears: {
                ...state.vehicleYears,
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(VehicleActions.GetVehicleYearsSuccess, (state, { vehicleYears }) => {
        return {
            ...state,
            vehicleYears: yearAdapter.addOne(vehicleYears, {
                ...state.vehicleYears,
                effectState: EffectLoadingState.Loaded,
            }),
        };
    }),
    on(VehicleActions.GetVehicleYearsFail, (state, { errorMessage }) => {
        return {
            ...state,
            vehicleYears: {
                ...state.vehicleYears,
                effectState: { errorMessage },
            },
        };
    }),
    on(VehicleActions.AddVehicleSelectYear, (state, { makeId, year }) => {
        return {
            ...state,
            vehicleModels: {
                ...state.vehicleModels,
                selectedMakeYearId: `${makeId}${year}`,
            },
        };
    }),
    on(VehicleActions.GetVehicleModels, (state) => {
        return {
            ...state,
            vehicleModels: {
                ...state.vehicleModels,
                effectState: EffectLoadingState.Loading,
            },
        };
    }),
    on(VehicleActions.GetVehicleModelsSuccess, (state, { vehicleModels }) => {
        return {
            ...state,
            vehicleModels: modelAdapter.addOne(vehicleModels, {
                ...state.vehicleModels,
                effectState: EffectLoadingState.Loaded,
            }),
        };
    }),
    on(VehicleActions.GetVehicleModelsFail, (state, { errorMessage }) => {
        return {
            ...state,
            vehicleModels: {
                ...state.vehicleModels,
                effectState: { errorMessage },
            },
        };
    })
);

// store module has to be added to the root of the module.
export const vehicleStoreModule = StoreModule.forFeature(
    vehicleFeatureKey,
    vehicleReducer
);

export function GetEarliestAlert(
    alerts: ActiveAlertContract[]
): ActiveAlertContract | null {
    if (alerts.length) {
        return alerts.reduce((prev, curr) => {
            if (prev.creationDate && curr.creationDate) {
                return prev.creationDate > curr.creationDate ? curr : prev;
            }
            return prev;
        });
    } else {
        return null;
    }
}
